import React, { useState, useEffect } from 'react';
import { playerAttributeService } from '../../services/playerAttributeService';
import { playerCardService } from '../../services/playerCardService';
import type { PlayerAttribute } from '../../types/playerCard';
import { getAttributeDefinitions, getRequiredTechnicalAttributes, getOptionalTechnicalAttributes } from '../../types/playerCard';
import { generateOffenseDefenseDesignations } from '../../utils/playerDesignations/index';
import { Star, Save, RefreshCw, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface AttributeEditorProps {
  cardId: string;
  sport: string;
  attributes: PlayerAttribute[];
  onUpdate: () => void;
}

export const AttributeEditor: React.FC<AttributeEditorProps> = ({
  cardId,
  sport,
  attributes,
  onUpdate,
}) => {
  const [localAttributes, setLocalAttributes] = useState<PlayerAttribute[]>(attributes);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    setLocalAttributes(attributes);
  }, [attributes]);

  const attributeDefinitions = getAttributeDefinitions(sport);
  const requiredTechnical = getRequiredTechnicalAttributes(sport);
  const optionalTechnical = getOptionalTechnicalAttributes(sport);
  // Sports that support required/optional technical attributes (like Football, Baseball, Softball)
  const supportsOptionalAttributes = optionalTechnical.length > 0;
  
  // Get currently set optional attributes
  const setOptionalAttributes = localAttributes
    .filter(a => a.category === 'technical' && optionalTechnical.includes(a.attribute_key))
    .map(a => a.attribute_key);
  
  // Get available optional attributes that aren't set yet
  const availableOptionalAttributes = optionalTechnical.filter(key => !setOptionalAttributes.includes(key));

  const handleAttributeChange = (category: 'physical' | 'technical' | 'mental', key: string, value: number) => {
    setLocalAttributes((prev) => {
      // Find the exact attribute matching both category AND key
      const existingIndex = prev.findIndex(
        a => a.category === category && a.attribute_key === key
      );
      
      if (existingIndex !== -1) {
        // Update existing attribute
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          value,
          updated_at: new Date().toISOString(),
        };
        return updated;
      } else {
        // Create new attribute entry
        return [
          ...prev,
          {
            id: '', // Will be set by server
            player_card_id: cardId,
            sport,
            category,
            attribute_key: key,
            attribute_name: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            value,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as PlayerAttribute,
        ];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Batch update all changed attributes
      const updates = localAttributes.map(attr => ({
        category: attr.category,
        attributeKey: attr.attribute_key,
        value: attr.value,
      }));

      await playerAttributeService.batchUpdateAttributes(cardId, sport, updates);
      
      // Get player card to access positions and role types for designation calculation
      const playerCard = await playerCardService.getPlayerCard(cardId);
      
      if (playerCard) {
        // Recalculate OVR after saving attributes
        try {
          await playerCardService.calculateOVR(cardId, sport);
        } catch (ovrError) {
          console.error('Error calculating OVR:', ovrError);
          // Don't fail the save if OVR calculation fails
        }

        // Generate and save offense/defense designations
        try {
          const designations = generateOffenseDefenseDesignations(
            localAttributes,
            playerCard.eligible_positions || [],
            playerCard.role_types || [],
            sport
          );

          await playerCardService.updatePlayerCard(cardId, {
            offense_designation: designations.offense_designation || undefined,
            defense_designation: designations.defense_designation || undefined,
          });
        } catch (designationError) {
          console.error('Error updating designations:', designationError);
          // Don't fail the save if designation update fails
        }
      }
      
      toast.success('Attributes saved successfully');
      onUpdate();
    } catch (error) {
      console.error('Error saving attributes:', error);
      toast.error('Failed to save attributes');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeDefaults = async () => {
    setInitializing(true);
    try {
      await playerAttributeService.initializeDefaultAttributes(cardId, sport);
      toast.success('Default attributes initialized');
      onUpdate();
    } catch (error) {
      console.error('Error initializing attributes:', error);
      toast.error('Failed to initialize attributes');
    } finally {
      setInitializing(false);
    }
  };

  const getAttributeValue = (category: 'physical' | 'technical' | 'mental', key: string): number => {
    const attr = localAttributes.find(a => a.category === category && a.attribute_key === key);
    return attr?.value || 5; // Default to 5 if not set
  };

  const renderAttributeSlider = (
    category: 'physical' | 'technical' | 'mental',
    key: string,
    label: string
  ) => {
    const value = getAttributeValue(category, key);
    // Create a unique key for React to ensure proper rendering
    const uniqueKey = `${category}-${key}`;
    
    return (
      <div key={uniqueKey} className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <span className="text-sm font-semibold text-primary">{value}/10</span>
        </div>
        <input
          type="range"
          min="1"
          max="10"
          value={value}
          onChange={(e) => {
            const newValue = parseInt(e.target.value);
            handleAttributeChange(category, key, newValue);
          }}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>1</span>
          <span>5</span>
          <span>10</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Player Attributes</h3>
        <div className="flex gap-2">
          {attributes.length === 0 && (
            <button
              onClick={handleInitializeDefaults}
              disabled={initializing}
              className="btn-secondary flex items-center gap-2"
            >
              <RefreshCw size={16} className={initializing ? 'animate-spin' : ''} />
              Reset
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Physical Attributes */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Star className="text-orange-500" size={18} />
          Physical Attributes
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {attributeDefinitions.physical.map((key) =>
            renderAttributeSlider('physical', key, key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
          )}
        </div>
      </div>

      {/* Technical Attributes */}
      {attributeDefinitions.technical.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
              <Star className="text-blue-500" size={18} />
              Technical Attributes
            </h4>
            {supportsOptionalAttributes && availableOptionalAttributes.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAttributeChange('technical', e.target.value, 5);
                      e.target.value = '';
                    }
                  }}
                  className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Add Optional Attribute...</option>
                  {availableOptionalAttributes.map(key => (
                    <option key={key} value={key}>
                      {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Required Technical Attributes */}
          {/* For sports with required/optional technical attributes (Football/Baseball/Softball), render the required section.
              For sports without required/optional structure (e.g., Basketball), we render via the fallback below. */}
          {supportsOptionalAttributes && requiredTechnical.length > 0 && (
            <div className="mb-6">
              <p className="text-xs text-gray-500 mb-3">Required for all positions</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {requiredTechnical.map((key) =>
                  renderAttributeSlider('technical', key, key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
                )}
              </div>
            </div>
          )}
          
          {/* Optional Technical Attributes (Position-specific) */}
          {supportsOptionalAttributes && setOptionalAttributes.length > 0 && (
            <div className="border-t pt-6">
              <p className="text-xs text-gray-500 mb-3">Optional - Position-specific (only affects OVR if set)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {setOptionalAttributes.map((key) => {
                  const value = getAttributeValue('technical', key);
                  const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                  return (
                    <div key={`optional-${key}`} className="space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          {label}
                          <span className="ml-2 text-xs text-gray-500">(Optional)</span>
                        </label>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await playerAttributeService.deleteAttribute(cardId, sport, 'technical', key);
                              setLocalAttributes(prev => prev.filter(a => !(a.category === 'technical' && a.attribute_key === key)));
                              toast.success('Attribute removed');
                              onUpdate();
                            } catch (error) {
                              console.error('Error removing attribute:', error);
                              toast.error('Failed to remove attribute');
                            }
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove this optional attribute"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={value}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          handleAttributeChange('technical', key, newValue);
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>1</span>
                        <span className="font-semibold text-primary">{value}/10</span>
                        <span>10</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Fallback for sports without optional attributes */}
          {!supportsOptionalAttributes && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {attributeDefinitions.technical.map((key) =>
                renderAttributeSlider('technical', key, key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
              )}
            </div>
          )}
        </div>
      )}

      {/* Mental Attributes */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Star className="text-purple-500" size={18} />
          Mental Attributes
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {attributeDefinitions.mental.map((key) =>
            renderAttributeSlider('mental', key, key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
          )}
        </div>
      </div>
    </div>
  );
};

